/**
 * A family of catalog keys built at runtime, declared with every member it
 * can produce: `template` holds `{0}`, `{1}` … where the parts go, and each
 * tuple of `tuples` fills them once. The marketing site builds a key from a
 * list (a bento cell, a story step, a partner type) only through its family,
 * so a guard reading the families (locales/keyFamilies.js) knows every key
 * the site can reach, and a catalog key no family and no literal produces is
 * one nothing renders.
 *
 * `members` lists the filled tuples in order, each once. `key(...parts)`
 * fills the template; in development and tests it refuses a key the family
 * does not declare (a list that gained an entry without its family, which
 * would otherwise print a raw id), and in production builds it fills the
 * template unchecked.
 */
export function keyFamily(template, tuples) {
    const fill = (parts) => template.replace(/\{(\d+)\}/g, (_, index) => String(parts[index]));
    const members = Object.freeze([...new Set(tuples.map(fill))]);
    const known = new Set(members);

    return Object.freeze({
        template,
        members,
        key(...parts) {
            const filled = fill(parts);
            if (import.meta.env.DEV && !known.has(filled)) {
                throw new Error(`${filled} is not a member of ${template}`);
            }

            return filled;
        },
    });
}

/** Every combination of one item from each list, in order, the first list outermost. */
export function cross(...lists) {
    return lists.reduce((combinations, list) => combinations.flatMap((combination) => list.map((item) => [...combination, item])), [[]]);
}
